export type DetailWindow = {
  to: number;
  truncated: boolean;
  nextFrom?: number;
  reason?: "slide cap" | "payload budget";
};

/*
 * How many slides of DETAIL fit in one inspect_deck reply.
 *
 * The cap used to count SLIDES only. On a firm master that is the wrong unit:
 * KPMG Law's has three decorative map slides of 164/247/267 shapes which alone
 * were 73% of a 114,739-char page, so "15 slides" bought the agent almost no
 * usable information and silently hid the other 44 slides. Bounding by payload
 * keeps a page useful whatever the slides happen to contain.
 *
 * `sizes` is 0-indexed over the whole deck; `from`/`to` are 1-based inclusive.
 * Pure, unit-tested — the Office.js handler around it cannot be.
 */
export function planDetailWindow(
  sizes: number[],
  from: number,
  slideCap: number,
  byteBudget: number
): DetailWindow {
  const lastSlide = sizes.length;
  const capLimit = Math.min(lastSlide, from + slideCap - 1);

  let to = from - 1;
  let spent = 0;
  let budgetStopped = false;

  for (let slide = from; slide <= capLimit; slide++) {
    const size = sizes[slide - 1];
    // Always take the first slide, even if it alone busts the budget —
    // returning zero slides would tell the agent nothing at all.
    if (slide > from && spent + size > byteBudget) {
      budgetStopped = true;
      break;
    }
    spent += size;
    to = slide;
  }

  if (to >= lastSlide) return { to, truncated: false };
  if (to < from) return { to, truncated: false };

  return {
    to,
    truncated: true,
    nextFrom: to + 1,
    reason: budgetStopped ? "payload budget" : "slide cap",
  };
}
